import { useCallback } from 'react';
import { AccessTypeT, ScopeT } from 'business/definitions';
import { USER_CREATION_FORM_ACTION_TYPE } from '../../../CreateUserContext/types';
import { useUserCreationFormDispatchContext } from '../../../CreateUserContext/CreateUserFormContext';

/**
 * Create the function to update or remove the access of a user to a scope.
 * @param {ScopeT} scope - The scope configuration
 * @returns {(accessType: AccessTypeT) => void} A function to update the access type of a scope.
 */
export const useGetAccessTypeUpdater = (scope: ScopeT) => {
    const { dispatchUserCreationForm } = useUserCreationFormDispatchContext();

    return useCallback(
        (accessType: AccessTypeT) => {
            switch (accessType) {
                case 'no_access':
                    dispatchUserCreationForm({
                        type: USER_CREATION_FORM_ACTION_TYPE.removeScope,
                        scope: { ...scope, accessType },
                    });
                    break;
                case 'restricted':
                case 'full_access':
                    dispatchUserCreationForm({
                        type: USER_CREATION_FORM_ACTION_TYPE.updateScopeAccessType,
                        scope: { ...scope, accessType },
                    });
                    break;
                default:
                    break;
            }
        },
        [dispatchUserCreationForm, scope],
    );
};

/**
 * Create the function to update or remove the access of a user to a write scope.
 * @param {ScopeT} scope - The scope configuration
 * @returns {(accessType: AccessTypeT) => void} A function to update the access type of a write scope.
 */
export const useGetAccessTypeUpdaterWriteScope = (scope: ScopeT) => {
    const { dispatchUserCreationForm } = useUserCreationFormDispatchContext();

    return useCallback(
        (accessType: AccessTypeT) => {
            switch (accessType) {
                case 'no_access':
                    dispatchUserCreationForm({
                        type: USER_CREATION_FORM_ACTION_TYPE.removeWriteScope,
                        writeScope: { ...scope, accessType },
                    });
                    break;
                case 'restricted':
                case 'full_access':
                    dispatchUserCreationForm({
                        type: USER_CREATION_FORM_ACTION_TYPE.updateWriteScopeAccessType,
                        writeScope: { ...scope, accessType },
                    });
                    break;
                default:
                    break;
            }
        },
        [dispatchUserCreationForm, scope],
    );
};
